import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FEATURE_KEYS,
  type NotificationList,
  type NotificationSettings,
  type UpdateNotificationSettingsInput,
  type WebPushConfig,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { useScopedClientId } from "@/hooks/useScopedClientId";
import { useCurrentUserProfile } from "@/hooks/useUsers";

const BASE = "/crm/notifications";

/**
 * How often the bell asks for news. The service worker makes a push land
 * instantly (it tells the open tab to refetch); this is the floor for a browser
 * that never allowed push.
 */
export const NOTIFICATIONS_POLL_MS = 30_000;

/** Where the CRM registers its service worker (`public/sw.js`). */
const SERVICE_WORKER_URL = "/sw.js";

/**
 * Whether THIS user has a bell: the client's plan grants «Notificaciones web»
 * and they are on the school's team. Super-admins and account managers are
 * never recipients, so they get no bell even while viewing an entitled school.
 */
export function useNotificationsAvailable(): boolean {
  const profile = useCurrentUserProfile();
  const { data: features } = useClientFeatures();
  const entitled = (features ?? []).includes(FEATURE_KEYS.webNotifications);
  return entitled && (profile?.role === "admin" || profile?.role === "member");
}

export function useNotificationSettings() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["notification-settings", clientId],
    queryFn: () => api.get<NotificationSettings>(`${BASE}/settings`),
    enabled: !!clientId,
  });
}

export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  const clientId = useScopedClientId();
  return useMutation({
    mutationFn: (patch: UpdateNotificationSettingsInput) =>
      api.put<NotificationSettings>(`${BASE}/settings`, patch),
    onSuccess: (data) => {
      queryClient.setQueryData(["notification-settings", clientId], data);
      toast.success("Notificaciones guardadas.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useNotifications(enabled: boolean) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["notifications", clientId],
    queryFn: () => api.get<NotificationList>(BASE),
    enabled: enabled && !!clientId,
    refetchInterval: NOTIFICATIONS_POLL_MS,
  });
}

export function useMarkNotificationsSeen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (upTo: string) => api.post<{ updated: number }>(`${BASE}/seen`, { upTo }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

/** VAPID public keys travel base64url; `PushManager.subscribe` wants the bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  // Backed by a plain ArrayBuffer: `applicationServerKey` refuses a view that
  // could sit on a SharedArrayBuffer.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register(SERVICE_WORKER_URL);
  return navigator.serviceWorker.ready;
}

export type WebPushState =
  /** This browser cannot do Web Push (old browser, or iOS outside the home-screen app). */
  | "unsupported"
  /** The server has no push configured, or the plan does not grant it. */
  | "unavailable"
  /** The person blocked notifications for this site in the browser. */
  | "denied"
  | "off"
  | "on";

/**
 * This browser's Web Push subscription. The permission prompt is only ever
 * opened from `enable()`, i.e. from a click — browsers ignore (and Chrome
 * penalises) a prompt that appears on its own.
 */
export function useWebPush(enabled: boolean) {
  const clientId = useScopedClientId();
  const { data: config } = useQuery({
    queryKey: ["push-config", clientId],
    queryFn: () => api.get<WebPushConfig>(`${BASE}/push-config`),
    enabled: enabled && !!clientId && pushSupported(),
    staleTime: 5 * 60_000,
  });
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  // On load, re-send an existing subscription. It binds this browser to the
  // person logged in NOW — a shared computer must not keep delivering the last
  // user's notifications — and re-creates a row the server deleted on a 410.
  useEffect(() => {
    if (!config?.enabled || !pushSupported() || Notification.permission !== "granted") return;
    let cancelled = false;
    void (async () => {
      try {
        const registration = await getRegistration();
        const existing = await registration.pushManager.getSubscription();
        if (cancelled) return;
        setSubscribed(existing !== null);
        if (existing) await api.post(`${BASE}/push-subscriptions`, existing.toJSON());
      } catch {
        // Best-effort: the bell still works by polling.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config?.enabled]);

  const state: WebPushState = !pushSupported()
    ? "unsupported"
    : !config?.enabled || !config.publicKey
      ? "unavailable"
      : Notification.permission === "denied"
        ? "denied"
        : subscribed && Notification.permission === "granted"
          ? "on"
          : "off";

  const enable = useCallback(async (): Promise<void> => {
    if (!config?.publicKey) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("El navegador no permitió las notificaciones.");
        return;
      }
      const registration = await getRegistration();
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.publicKey),
        }));
      await api.post(`${BASE}/push-subscriptions`, subscription.toJSON());
      setSubscribed(true);
      toast.success("Notificaciones activadas en este navegador.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudieron activar las notificaciones.");
    } finally {
      setBusy(false);
    }
  }, [config?.publicKey]);

  const disable = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const registration = await getRegistration();
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.delete(`${BASE}/push-subscriptions`, { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notificaciones desactivadas en este navegador.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudieron desactivar.");
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, enable, disable };
}

/**
 * A push that arrives while a CRM tab is focused is not shown by the OS: the
 * service worker hands it to the tab instead (`sw.js`), and the tab refetches
 * the bell — which is what fires the toast, through the one toast path.
 */
export function useServiceWorkerRefetch(enabled: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent): void => {
      const data: unknown = event.data;
      if (typeof data === "object" && data !== null && "type" in data && data.type === "notification") {
        void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [enabled, queryClient]);
}
